// Style as confirm component
import { unit } from '@ant-design/cssinjs';

import { prepareComponentToken, prepareToken } from '.';
import type { ModalToken } from '.';
import { clearFix } from '../../style';
import { genSubStyleComponent } from '../../theme/internal';
import type { GenerateStyle } from '../../theme/internal';

// ============================= Confirm ==============================

const genModalConfirmStyle: GenerateStyle<ModalToken> = (token) => {
  const {
    componentCls,
    titleFontSize,
    titleLineHeight,
    modalConfirmIconSize,
    fontSize,
    lineHeight,
    modalTitleHeight,
    fontHeight,
    confirmBodyPadding,
  } = token;
  const confirmComponentCls = `${componentCls}-confirm`;

  return {
    [confirmComponentCls]: {
      '&-rtl': {
        direction: 'rtl',
      },
      [`${token.antCls}-modal-header`]: {
        display: 'none',
      },
      [`${confirmComponentCls}-body-wrapper`]: {
        ...clearFix(),
      },
      [`&${componentCls} ${componentCls}-body`]: {
        padding: confirmBodyPadding,
      },

      // ====================== Body ======================
      [`${confirmComponentCls}-body`]: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        alignItems: 'start',

        [`> ${token.iconCls}, > ${confirmComponentCls}-icon-wrapper`]: {
          flex: 'none',
          fontSize: modalConfirmIconSize,
          marginBlockEnd: token.confirmIconMarginInlineEnd,
          marginTop: token
            .calc(token.calc(fontHeight).sub(modalConfirmIconSize).equal())
            .div(2)
            .equal(),
        },
        [`&-has-title > ${token.iconCls}, &-has-title > ${confirmComponentCls}-icon-wrapper`]: {
          marginTop: token
            .calc(token.calc(modalTitleHeight).sub(modalConfirmIconSize).equal())
            .div(2)
            .equal(),
        },
      },

      [`${confirmComponentCls}-icon-wrapper`]: {
        display: 'flex',
        padding: 8,
        borderRadius: '50%',
      },

      [`${confirmComponentCls}-paragraph`]: {
        display: 'flex',
        flexDirection: 'column',
        flex: 'auto',
        rowGap: token.marginXS,
        // https://github.com/ant-design/ant-design/issues/51912
        maxWidth: `calc(100% - ${unit(token.marginSM)})`,
      },

      // https://github.com/ant-design/ant-design/issues/48159
      [`${token.iconCls} + ${confirmComponentCls}-paragraph`]: {
        maxWidth: `calc(100% - ${unit(
          token.calc(token.modalConfirmIconSize).add(token.marginSM).equal(),
        )})`,
      },

      [`${confirmComponentCls}-title`]: {
        color: token.colorTextHeading,
        fontWeight: token.fontWeightStrong,
        fontSize: titleFontSize,
        lineHeight: titleLineHeight,
      },

      [`${confirmComponentCls}-content`]: {
        color: token.colorText,
        fontSize,
        lineHeight,
      },

      // ===================== Footer =====================
      [`${confirmComponentCls}-btns`]: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 32,

        [`${token.antCls}-btn + ${token.antCls}-btn`]: {
          marginBottom: 0,
          marginInlineStart: token.marginXS,
        },
      },
    },

    [`${confirmComponentCls}-confirm ${confirmComponentCls}-body >  ${confirmComponentCls}-icon-wrapper`]:
      {
        color: '#B45309',
        backgroundColor: '#FEF3C7',
      },

    [`${confirmComponentCls}-error ${confirmComponentCls}-body >  ${confirmComponentCls}-icon-wrapper`]:
      {
        color: '#A41616',
        backgroundColor: '#FEE2E2',
      },

    [`${confirmComponentCls}-warning ${confirmComponentCls}-body >  ${confirmComponentCls}-icon-wrapper`]:
      {
        color: '#B45309',
        backgroundColor: '#FEF3C7',
      },

    [`${confirmComponentCls}-progressing ${confirmComponentCls}-body >  ${confirmComponentCls}-icon-wrapper`]:
      {
        color: '#2B6FF6',
        backgroundColor: '#F3F7FF',
      },

    [`${confirmComponentCls}-info ${confirmComponentCls}-body >  ${confirmComponentCls}-icon-wrapper`]:
      {
        color: '#1E40AF',
        backgroundColor: '#DBEAFE',
      },

    [`${confirmComponentCls}-success ${confirmComponentCls}-body >  ${confirmComponentCls}-icon-wrapper`]:
      {
        color: '#166534',
        backgroundColor: '#DCFCE7',
      },
  };
};

// ============================== Export ==============================
export default genSubStyleComponent(
  ['Modal', 'confirm'],
  (token) => {
    const modalToken = prepareToken(token);

    return [genModalConfirmStyle(modalToken)];
  },
  prepareComponentToken,
  {
    // confirm is weak than modal since no conflict here
    order: -1000,
  },
);
